import React from 'react';
import { motion } from 'framer-motion';

const RacePlannerTitle = () => {
    return (
        <header className="mb-6 relative w-full p-8 md:p-12 rounded-[32px] bg-white/60 dark:bg-[#0B0F19]/60 backdrop-blur-2xl border border-slate-200/50 dark:border-white/5 shadow-[0_8px_32px_rgba(0,0,0,0.05)] dark:shadow-[0_8px_32px_rgba(0,0,0,0.5)] overflow-hidden">
            {/* Ambient Background Glows para Masterpiece Depth */}
            <motion.div 
                className="absolute top-0 left-[-10%] w-[300px] h-[300px] md:w-[400px] md:h-[400px] bg-[#48E5B9] rounded-full blur-[100px] md:blur-[120px] mix-blend-multiply dark:mix-blend-screen opacity-30 dark:opacity-10 pointer-events-none"
                animate={{ 
                    scale: [1, 1.3, 1],
                    x: [0, 40, 0],
                    y: [0, -20, 0]
                }}
                transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
            />
            <motion.div 
                className="absolute top-[20%] left-[10%] w-[250px] h-[250px] md:w-[350px] md:h-[350px] bg-[#FAB0C0] rounded-full blur-[80px] md:blur-[100px] mix-blend-multiply dark:mix-blend-screen opacity-30 dark:opacity-10 pointer-events-none"
                animate={{ 
                    scale: [1.2, 1, 1.2],
                    x: [0, -30, 0],
                    y: [0, 30, 0]
                }}
                transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
            />

            {/* Asymmetrical High-Tech line */}
            <motion.div
                initial={{ width: 0, opacity: 0 }}
                animate={{ width: '120px', opacity: 1 }}
                transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
                className="relative mb-10 h-px bg-gradient-to-r from-[#48E5B9] via-[#FAB0C0]/50 to-transparent overflow-hidden"
            >
                <motion.div 
                    className="absolute top-0 left-0 h-full w-[30px] bg-slate-800 dark:bg-white opacity-40 dark:opacity-80 shadow-[0_0_10px_rgba(15,23,42,0.8)] dark:shadow-[0_0_10px_white]"
                    animate={{ x: ['-100%', '400%'] }}
                    transition={{ duration: 2.5, repeat: Infinity, ease: 'linear' }}
                />
            </motion.div>

            <div className="relative isolate z-10 w-full md:w-max mt-4">
                {/* Texto de Fondo (Eco) - Brutalist & Asymmetrical */}
                <motion.span 
                    animate={{ 
                        y: [-5, 5, -5],
                        opacity: [0.03, 0.06, 0.03],
                        scale: [0.98, 1, 0.98]
                    }}
                    transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
                    className="absolute -top-12 -left-4 md:-left-8 text-[100px] md:text-[200px] font-black text-slate-900 dark:text-white select-none pointer-events-none uppercase tracking-tighter leading-none z-0"
                >
                    Plan
                </motion.span>
                
                <motion.div
                    animate={{ y: [-4, 4, -4] }}
                    transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
                    className="relative z-10 pl-2 md:pl-0"
                >
                    <motion.h1 
                        initial={{ x: -40, opacity: 0, filter: 'blur(12px)' }}
                        animate={{ x: 0, opacity: 1, filter: 'blur(0px)' }}
                        transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
                        className="text-7xl md:text-9xl font-black tracking-tighter leading-none flex flex-col gap-2"
                    >
                        <span className="text-transparent [-webkit-text-stroke:2px_rgba(15,23,42,0.8)] dark:[-webkit-text-stroke:2px_rgba(255,255,255,0.6)] drop-shadow-[0_0_10px_rgba(15,23,42,0.1)] dark:drop-shadow-[0_0_20px_rgba(255,255,255,0.15)] transition-all">
                            Race
                        </span>
                        <span className="bg-clip-text text-transparent bg-gradient-to-r from-slate-900 dark:from-[#FAB0C0] via-[#FAB0C0] dark:via-[#48E5B9] to-[#48E5B9] dark:to-[#FAB0C0] animate-gradient-xy relative inline-block drop-shadow-[0_0_15px_rgba(72,229,185,0.2)] dark:drop-shadow-[0_0_25px_rgba(72,229,185,0.4)] ml-8 md:ml-16">
                            Planner
                        </span>
                    </motion.h1>
                </motion.div>
            </div>

            {/* Visual Geometric Ornament (Replaces empty text block) */}
            <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6, duration: 1 }}
                className="mt-12 sm:mt-16 ml-0 md:ml-auto md:mr-16 w-full md:w-[300px] flex flex-col items-end gap-3 relative z-20"
            >
                {/* Animated Orbs and Lines */}
                <div className="flex items-center gap-4 w-full justify-end">
                    <motion.div 
                        className="h-[1px] bg-gradient-to-r from-transparent via-slate-900/10 dark:via-white/10 to-slate-900/40 dark:to-white/40 grow origin-right"
                        animate={{ opacity: [0.3, 1, 0.3], scaleX: [0.2, 1, 0.2] }}
                        transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
                    />
                    <motion.div 
                        animate={{ scale: [1, 1.5, 1], filter: ['blur(1px)', 'blur(2px)', 'blur(1px)'] }}
                        transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                        className="w-1.5 h-1.5 rounded-full bg-[#48E5B9] shadow-[0_0_15px_rgba(72,229,185,0.4)] dark:shadow-[0_0_15px_rgba(72,229,185,0.8)]"
                    />
                    <motion.div 
                        animate={{ scale: [1.2, 0.8, 1.2], opacity: [0.5, 1, 0.5] }}
                        transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut', delay: 0.5 }}
                        className="w-1 h-1 rounded-full bg-slate-800 dark:bg-white shadow-[0_0_8px_rgba(15,23,42,0.4)] dark:shadow-[0_0_8px_white]"
                    />
                </div>
                
                <div className="flex items-center gap-6 w-[80%] justify-end">
                    <motion.div 
                        className="h-[1px] bg-gradient-to-r from-transparent to-[#FAB0C0]/20 dark:to-[#FAB0C0]/40 grow"
                        animate={{ x: [-20, 0, -20], opacity: [0.2, 0.8, 0.2] }}
                        transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                    />
                    <motion.div 
                        animate={{ rotate: 360 }}
                        transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
                        className="w-3 h-3 border border-slate-900/20 dark:border-white/20 rounded-sm flex items-center justify-center shrink-0"
                    >
                        <div className="w-1 h-1 bg-slate-900/40 dark:bg-white/40 rounded-full" />
                    </motion.div>
                </div>
            </motion.div>
        </header>
    );
};

export default RacePlannerTitle;
